import { Vector3 } from 'three'
import { ImmutableVector3, getFlatShapeNormal, toImmutable } from '@modugen/ui-toolkit'
import {
  scaleLineLoadForce,
  lineLoadForceFormatter,
  pointLoadForceFormatter,
  scalePointLoadForce,
  type LoadSceneLabelFormatOptions,
} from '../LoadMesh/utils'
import {
  loadPointOnDomain,
  magnitudeArrowDirection,
  scaleVector,
  localToGlobalForce,
} from '../utils'

interface LoadDisplayInputData {
  domainStart: Point
  domainEnd: Point
  scale: number
  domain: Domain
  labelFormat?: LoadSceneLabelFormatOptions
}
interface LineLoadDisplayInputData extends LoadDisplayInputData {
  load: LineLoad
  thickness?: number
}

interface PointLoadDisplayInputData extends LoadDisplayInputData {
  load: PointLoad
}

export const createLineLoadDisplayData = ({
  domain,
  load,
  domainStart,
  domainEnd,
  scale,
  thickness = 0.05,
  labelFormat,
}: LineLoadDisplayInputData) => {
  const { relative_end, relative_start, start, end } = load

  const loadStartPoint = loadPointOnDomain(relative_start, domainStart, domainEnd)
  const loadEndPoint = loadPointOnDomain(relative_end, domainStart, domainEnd)
  // to global cooredinate
  const forceStart = localToGlobalForce(start, domain).multiplyScalar(-1)
  const forceEnd = localToGlobalForce(end, domain).multiplyScalar(-1)
  // scale load display size
  const scaledForceStart =
    forceStart.length() < 1.0e-3 ? new Vector3(0, 0, 0) : scaleVector(forceStart, scale)
  // this ensure the geometry points are in the same plane when the force is too small
  const scaledForceEnd =
    forceEnd.length() < 1.0e-3 ? new Vector3(0, 0, 0) : scaleVector(forceEnd, scale)
  // get top ends of the shape
  const loadStartPointPlusForce = loadStartPoint.clone().add(scaledForceStart)
  const loadEndPointPlusForce = loadEndPoint.clone().add(scaledForceEnd)
  // points that will form the shape
  const points = [loadStartPoint, loadEndPoint, loadEndPointPlusForce, loadStartPointPlusForce]
  // for the directional arrow display
  const loadStartMagnitudePoint = magnitudeArrowDirection(start, points[0], points[3])
  const loadEndMagnitudePoint = magnitudeArrowDirection(start, points[1], points[2])
  const normal = getFlatShapeNormal(
    points.map(p => toImmutable(p)) as [
      ImmutableVector3,
      ImmutableVector3,
      ImmutableVector3,
      ...ImmutableVector3[],
    ],
  )
  const shapePointsThicknessAdjusted = points.map(p =>
    toImmutable(p.addScaledVector(normal.v, thickness / -2.0)),
  )

  return {
    loadType: 'line-load',
    label: lineLoadForceFormatter(scaleLineLoadForce(scale, scaledForceStart.z), labelFormat),
    endLabel: lineLoadForceFormatter(scaleLineLoadForce(scale, scaledForceEnd.z), labelFormat),
    magnitudePoints: [loadStartMagnitudePoint, loadEndMagnitudePoint],
    shapeObject: {
      shape: {
        points: shapePointsThicknessAdjusted,
      },
      openings: [],
      domains: [],
      thickness,
      storey: '',
    },
  }
}

export const createPointLoadDisplayData = ({
  load,
  domainStart,
  domainEnd,
  scale,
  domain,
  labelFormat,
}: PointLoadDisplayInputData) => {
  const { relative_position, force } = load

  const loadPoint = loadPointOnDomain(relative_position, domainStart, domainEnd)
  const globalForce = localToGlobalForce(force, domain).multiplyScalar(-1) // converForceFromLocalToGlobal(force, selectedDomain).multiplyScalar(-1)
  const scaledGlobalForce = scaleVector(globalForce, scale)
  const loadPointPlusForce = loadPoint.clone().add(scaledGlobalForce)
  const loadMagnitudePoint = magnitudeArrowDirection(force, loadPoint, loadPointPlusForce)

  return {
    loadType: 'point-load',
    label: pointLoadForceFormatter(scalePointLoadForce(scale, scaledGlobalForce.z), labelFormat),
    magnitudePoints: [loadMagnitudePoint],
  }
}
